import { useMemo, useState } from "react";

/**
 * Phase 18 — Generic admin filter engine.
 *
 * Usage:
 *   const filters = useFilters(rows, [
 *     { key: "search",  type: "search", paths: ["name", "email"] },
 *     { key: "status",  type: "select", path: "status", options: [...] },
 *     { key: "dateFrom", type: "dateFrom", path: "created_at" },
 *     { key: "dateTo",   type: "dateTo",   path: "created_at" },
 *     { key: "archived", type: "boolean", path: "is_archived" },
 *   ], { defaultSort: { field: "created_at", dir: "desc" } });
 *
 *   filters.values      — current filter state object
 *   filters.set         — (key, value) updater
 *   filters.clear       — clears every filter
 *   filters.filtered    — filtered + sorted rows
 *   filters.count       — filtered length
 *   filters.activeCount — number of non-empty filters (for "Clear (n)" affordance)
 *   filters.sort, filters.setSort — { field, dir }
 *
 * Filtering is done client-side on the already-loaded list. Server-side
 * filtering (audit_logs) is layered on top by passing the query state up
 * to the page-level fetch.
 */

function getPath(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

function matchesFilter(row, schema, value) {
  if (value === undefined || value === null || value === "" || value === "all") return true;
  const v = getPath(row, schema.path);

  switch (schema.type) {
    case "search": {
      const q = String(value).toLowerCase().trim();
      if (!q) return true;
      return (schema.paths || [schema.path]).some((p) => {
        const cell = getPath(row, p);
        if (cell == null) return false;
        return String(cell).toLowerCase().includes(q);
      });
    }
    case "select": {
      if (Array.isArray(v)) return v.includes(value);
      return String(v ?? "") === String(value);
    }
    case "boolean":
      // "true" = field is truthy, "false" = field is explicitly falsy/missing
      if (value === "true") return !!v;
      if (value === "false") return !v;
      return true;
    case "dateFrom":
      return v ? new Date(v) >= new Date(value) : false;
    case "dateTo":
      return v ? new Date(v) <= new Date(`${value}T23:59:59`) : false;
    case "min":
      return Number(v) >= Number(value);
    case "max":
      return Number(v) <= Number(value);
    default:
      return true;
  }
}

export function useFilters(rows, schemas, opts = {}) {
  const initial = {};
  schemas.forEach((s) => { initial[s.key] = s.default ?? ""; });
  const [values, setValues] = useState(initial);
  const [sort, setSort] = useState(opts.defaultSort || null);

  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));
  const clear = () => setValues(initial);

  const filtered = useMemo(() => {
    // Auto-hide soft-deleted rows unless caller opts out or the user has
    // explicitly set is_deleted="all" / "true".
    const hideDeletedByDefault = opts.hideDeletedByDefault !== false;
    const deletedFilterState = values.is_deleted; // "", "true", "false", "all"
    const shouldAutoHide = hideDeletedByDefault
      && (deletedFilterState === undefined || deletedFilterState === "" || deletedFilterState === "false");

    const list = (rows || []).filter((row) => {
      if (shouldAutoHide && row.is_deleted === true) return false;
      return schemas.every((schema) => matchesFilter(row, schema, values[schema.key]));
    });
    if (sort?.field) {
      const dir = sort.dir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        const va = getPath(a, sort.field);
        const vb = getPath(b, sort.field);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    return list;
  }, [rows, schemas, values, sort, opts.hideDeletedByDefault]);

  const activeCount = schemas.reduce(
    (n, s) => n + (values[s.key] && values[s.key] !== "all" ? 1 : 0), 0
  );

  return { values, set, clear, filtered, count: filtered.length, activeCount, sort, setSort };
}
